'use client';
import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
} from '@ui/components/ui/dropdown-menu';
import { useMessageContext } from '@ui/components/organisms/message/message.context';
import type { IMessage } from '@ui/components/organisms/message/message.typings';
import type { ContextUserType } from '@ui/constants/context';
import { Button } from '@ui/components/ui/button';
import { Ellipsis, Flag, PencilOff, Trash, PinIcon, Share2 } from 'lucide-react';
import { useReportContent } from '@ui/contexts/report-content-context';
import { useInteractionGuard } from '@hooks/hooks/useInteractionGuard';
import { ShareModal } from '@ui/components/organisms/share-modal';
import { DynamicRouteBuilder } from '@ui/constants/dynamic-route-builder';
import { usePostHogFeatureFlag } from '@ui/hooks/posthog/usePostHogFeatureFlag';
import {
  trackCommunityPostShared,
  trackCommunityPostReported,
  trackCommunityPostEditStarted,
  trackCommunityPostDeleted,
  trackCommunityPostPinned,
} from '@ui/analytics/community-events';
import dynamic from 'next/dynamic';

const PostItemDeleteActionModal = dynamic(() =>
  import('./post-item-delete-action-modal').then((module) => module.PostItemDeleteActionModal),
);

interface Props {
  post: IMessage;
  userType: ContextUserType;
  userId: string;
  enableContentFlagging: boolean;
  reportLabel?: string;

  dropdownOpen: boolean;
  contentType?: string;
  isReply?: boolean;
  isTopLevelComment?: boolean;
  communityDetails: NonNullable<IMessage['community_details']>;
  moderators_ids: string[];
  onUpdateTrigger: () => void;
  setDropdownOpen: (open: boolean) => void;
  /**
   * When true, hide edit/delete actions (e.g. while a post is still processing).
   */
  disableEditDelete?: boolean;
}

export const PostItemsMoreOptions: React.FC<Props> = (props) => {
  const {
    post,
    userType,
    userId,
    enableContentFlagging,
    reportLabel,
    onUpdateTrigger,
    dropdownOpen,
    setDropdownOpen,
    contentType,
    isReply,
    isTopLevelComment,
    communityDetails,
    moderators_ids,
    disableEditDelete,
  } = props;

  const enableURLShareModal = usePostHogFeatureFlag('enable-url-share-modal');
  const dropDownContentRef = useRef<HTMLDivElement>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const messageState = useMessageContext(userType);
  const { openReportContent, onReasonSelected } = useReportContent();
  const { interceptInteraction } = useInteractionGuard();

  const joinMessage = useMemo(
    () =>
      communityDetails?.creator_display_name
        ? `Join community to use this feature. Collect this badge to join community!`
        : 'Join this KOR Space to use this feature. Collect this badge to join.',
    [communityDetails?.creator_display_name],
  );

  const hierarchyType: 'post' | 'comment' | 'reply' = isReply
    ? 'reply'
    : contentType === 'post'
    ? 'post'
    : 'comment';

  const onDeleteOpen = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDeleteModalOpen(true);
      trackCommunityPostDeleted({
        post_id: post.id,
        creator_id: communityDetails.creator_id,
        user_id: userId,
        user_type: userType,
        hierarchy_type: hierarchyType,
        is_moderator_action: moderators_ids?.includes(userId) && post.user_id !== userId,
        surface: userType === 'creator' ? 'creator' : 'fan',
      });
    },
    [
      post.id,
      post.user_id,
      communityDetails.creator_id,
      userId,
      userType,
      hierarchyType,
      moderators_ids,
    ],
  );

  useEffect(() => {
    const handleReasonSelected = (reason: string, message: IMessage) =>
      messageState.onReportMessage({ message_id: message.id, report: reason });
    onReasonSelected(handleReasonSelected);
  }, [onReasonSelected, messageState]);

  const onEdit = interceptInteraction((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackCommunityPostEditStarted({
      post_id: post.id,
      creator_id: communityDetails.creator_id,
      user_id: userId,
      user_type: userType,
      hierarchy_type: hierarchyType,
      surface: userType === 'creator' ? 'creator' : 'fan',
    });
    onUpdateTrigger();
    setDropdownOpen(false);
  }, joinMessage);

  const canEdit = useMemo(
    () => post.user_id === userId && !post.block && !disableEditDelete,
    [post.user_id, userId, post.block, disableEditDelete],
  );

  const isModerator = useMemo(
    () => moderators_ids?.includes(userId) || false,
    [moderators_ids, userId],
  );

  const isCommunityOwner = useMemo(
    () => userId === communityDetails.creator_id,
    [userId, communityDetails.creator_id],
  );
  const isBaseCommunity = useMemo(
    () => communityDetails.conversation_type === 'community',
    [communityDetails.conversation_type],
  );
  const isPostOwner = useMemo(() => userId === post.user_id, [userId, post.user_id]);

  const canDelete = useMemo(() => {
    if (disableEditDelete) {
      return false;
    }

    if (isPostOwner) {
      return true;
    }

    if (isCommunityOwner) {
      return true;
    }

    if (isModerator && !isBaseCommunity) {
      return true;
    }
    return false;
  }, [disableEditDelete, isCommunityOwner, isModerator, isBaseCommunity, isPostOwner]);

  const showDropdownTrigger = useMemo(() => {
    // For comments/replies that are still processing (no share option),
    // hide the three-dot menu entirely.
    if (disableEditDelete && contentType !== 'post') {
      return false;
    }

    return enableContentFlagging || userId === post.user_id || isModerator;
  }, [disableEditDelete, contentType, enableContentFlagging, userId, post.user_id, isModerator]);

  const shouldShowMenu = useMemo(() => {
    // For posts, we allow the menu if either share (feature flag) or
    // any of the owner/moderator triggers are available.
    if (contentType === 'post') {
      return enableURLShareModal || showDropdownTrigger;
    }
    // For comments/replies, only show when there are non-share actions.
    return showDropdownTrigger;
  }, [contentType, enableURLShareModal, showDropdownTrigger]);

  const showOnlyEditDelete = useMemo(() => post.draft, [post.draft]);

  const canPinComment = useMemo(
    () =>
      !contentType &&
      Boolean(isTopLevelComment) &&
      (isModerator || isCommunityOwner) &&
      !post.draft &&
      !post.block &&
      !disableEditDelete,
    [
      contentType,
      isTopLevelComment,
      isModerator,
      isCommunityOwner,
      post.draft,
      post.block,
      disableEditDelete,
    ],
  );

  const onPin = interceptInteraction((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(false);
    trackCommunityPostPinned({
      post_id: post.id,
      creator_id: communityDetails.creator_id,
      user_id: userId,
      user_type: userType,
      action: post.pinned_at ? 'unpin' : 'pin',
      surface: userType === 'creator' ? 'creator' : 'fan',
    });
    messageState.onPinMessage(
      post.id,
      post.pinned_at ? false : true,
      userId,
      post.thread_id,
      post.conversation_id,
      'Post',
    );
  }, joinMessage);

  const onPinComment = interceptInteraction((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(false);
    messageState.onPinMessage(
      post.id,
      !post.pinned_at,
      userId,
      post.thread_id,
      post.conversation_id,
      'Comment',
    );
  }, joinMessage);

  const onDelete = interceptInteraction(onDeleteOpen, joinMessage);
  const onReport = interceptInteraction(() => {
    trackCommunityPostReported({
      post_id: post.id,
      creator_id: communityDetails.creator_id,
      user_id: userId,
      user_type: userType,
      hierarchy_type: hierarchyType,
      surface: userType === 'creator' ? 'creator' : 'fan',
    });
    openReportContent(post);
  }, joinMessage);

  const onShareClick = useCallback(() => {
    trackCommunityPostShared({
      post_id: post.id,
      creator_id: communityDetails.creator_id,
      user_id: userId,
      user_type: userType,
      share_method: 'modal',
      surface: userType === 'creator' ? 'creator' : 'fan',
    });
  }, [post.id, communityDetails.creator_id, userId, userType]);

  return (
    <>
      <PostItemDeleteActionModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        post={post}
        userType={userType}
        isReply={isReply}
        isModerator={isModerator}
      />

      {shouldShowMenu && (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="rounded">
              <Ellipsis className="h-4 w-4 " />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-40" hideWhenDetached ref={dropDownContentRef}>
            <DropdownMenuGroup className="w-40">
              {!showOnlyEditDelete && contentType === 'post' && enableURLShareModal && (
                <DropdownMenuItem
                  className="space-x-2 w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onShareClick();
                  }}>
                  <ShareModal
                    url={`${process.env.NEXT_PUBLIC_FAN_WEB_URL}${DynamicRouteBuilder.postBySlug(
                      communityDetails.creator_display_name || communityDetails.creator_id,
                      post.conversation_id,
                      post.slug,
                    )}`}
                    title={`Check out ${communityDetails.creator_display_name}'s post on Kor`}>
                    <div className="flex items-center space-x-2 w-full min-w-full">
                      <Share2 className="h-4 w-4" />
                      <span>Share</span>
                    </div>
                  </ShareModal>
                </DropdownMenuItem>
              )}

              {showDropdownTrigger && (
                <>
                  {!showOnlyEditDelete &&
                    contentType &&
                    canEdit &&
                    userType === 'creator' &&
                    post.created_by_creator && (
                      <DropdownMenuItem className="space-x-2" onClick={onPin}>
                        <PinIcon className="h-4 w-4" />
                        {post.pinned_at ? <span>Unpin</span> : <span>Pin</span>}
                      </DropdownMenuItem>
                    )}

                  {canPinComment && (
                    <DropdownMenuItem className="space-x-2" onClick={onPinComment}>
                      <PinIcon className="h-4 w-4" />
                      {post.pinned_at ? <span>Unpin comment</span> : <span>Pin comment</span>}
                    </DropdownMenuItem>
                  )}

                  {canEdit && (
                    <DropdownMenuItem className="space-x-2" onClick={onEdit}>
                      <PencilOff className="h-4 w-4" />
                      <span>Edit</span>
                    </DropdownMenuItem>
                  )}

                  {canDelete && (
                    <DropdownMenuItem className="space-x-2" onClick={onDelete}>
                      <Trash className="h-4 w-4" />
                      <span>Delete</span>
                    </DropdownMenuItem>
                  )}

                  {!showOnlyEditDelete && enableContentFlagging && (
                    <DropdownMenuItem className="space-x-2" onClick={onReport}>
                      <Flag className="h-4 w-4" />
                      <span>{reportLabel}</span>
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
};
